import React, { useState } from 'react';
import { PlayCircle, FileText, CheckCircle, Clock, Lock, BookOpen, DollarSign, BarChart3, PieChart, Users, Briefcase, ChevronRight, Lightbulb } from 'lucide-react';
interface Module {
  title: string;
  type: 'video' | 'reading' | 'exercise';
  duration: string;
  completed: boolean;
}
interface Course {
  id: number;
  title: string;
  theme: string;
  outcome: string;
  keyShift: string;
  icon: React.ElementType;
  status: 'completed' | 'active' | 'locked';
  label: string;
  modules: Module[];
}
export function CoursesPage() {
  const [activeCourse, setActiveCourse] = useState(2);
  const courses: Course[] = [{
    id: 1,
    title: 'Strategic Hiring & Non-Dilutive Talent Funding',
    theme: 'Turn hiring into a financial advantage',
    outcome: 'Learn how to scale your team using government incentives and structured hiring strategy — not just cash burn.',
    keyShift: 'From: "Hiring is expensive" → To: "Hiring can extend runway"',
    icon: Users,
    status: 'completed',
    label: 'Course 1',
    modules: [{
      title: 'Hiring Against Revenue Milestones (Not Gut Feel)',
      type: 'video',
      duration: '40 min',
      completed: true
    }, {
      title: 'Contractors vs Employees: Financial & Tax Implications',
      type: 'video',
      duration: '35 min',
      completed: true
    }, {
      title: 'Technical vs Non-Technical Roles & SR&ED Eligibility',
      type: 'video',
      duration: '30 min',
      completed: true
    }, {
      title: 'Leveraging Programs: SR&ED, Wage Subsidies & IRAP',
      type: 'reading',
      duration: '25 min',
      completed: true
    }, {
      title: 'Documentation Systems for Claiming Credits',
      type: 'reading',
      duration: '20 min',
      completed: true
    }, {
      title: 'Exercise: Build a Funded Hiring Roadmap',
      type: 'exercise',
      duration: '90 min',
      completed: true
    }]
  }, {
    id: 2,
    title: 'Capital Strategy & Smart Financing Mix',
    theme: 'Raise smarter, not just faster',
    outcome: 'Understand how to sequence capital sources to minimize dilution and maximize growth.',
    keyShift: 'Exercise: Map how to raise $1M without giving up 25% equity',
    icon: DollarSign,
    status: 'completed',
    label: 'Course 2',
    modules: [{
      title: 'The Full Capital Stack: Equity, Debt, RBF, Grants & Credits',
      type: 'video',
      duration: '45 min',
      completed: true
    }, {
      title: 'When to Use What: Pre-Revenue vs Post-Revenue Strategies',
      type: 'video',
      duration: '35 min',
      completed: true
    }, {
      title: 'Blended Capital Strategy: Extending Runway Before Equity',
      type: 'video',
      duration: '40 min',
      completed: true
    }, {
      title: 'Common Pitfalls: SAFE Overuse & Debt Misuse',
      type: 'reading',
      duration: '20 min',
      completed: true
    }, {
      title: 'Investor Lens: Burn Multiple & Capital Efficiency Metrics',
      type: 'video',
      duration: '30 min',
      completed: true
    }, {
      title: 'Exercise: Raise $1M Without Giving Up 25% Equity',
      type: 'exercise',
      duration: '120 min',
      completed: true
    }]
  }, {
    id: 3,
    title: 'Unit Economics & Sustainable Growth',
    theme: 'Growth that actually makes money',
    outcome: 'Determine whether your business model truly works at scale.',
    keyShift: '"If it doesn\'t work at $1, it won\'t work at $1M"',
    icon: BarChart3,
    status: 'active',
    label: 'Course 3',
    modules: [{
      title: 'What "Good" Unit Economics Actually Means',
      type: 'video',
      duration: '35 min',
      completed: true
    }, {
      title: 'Why Growth Can Hide Broken Models',
      type: 'video',
      duration: '30 min',
      completed: true
    }, {
      title: 'Contribution Margin, CAC & LTV Fundamentals',
      type: 'video',
      duration: '45 min',
      completed: false
    }, {
      title: 'Diagnosing Economic Inefficiencies Early',
      type: 'reading',
      duration: '25 min',
      completed: false
    }, {
      title: 'Exercise: Scaling vs Amplifying Problems',
      type: 'exercise',
      duration: '90 min',
      completed: false
    }]
  }, {
    id: 4,
    title: 'Financial Literacy for Scale (Beyond Revenue)',
    theme: 'Understanding what investors actually care about',
    outcome: 'Gain clarity on financial signals of a scalable company — even before profitability.',
    keyShift: 'Not about being profitable yet — about being investable and credible',
    icon: PieChart,
    status: 'locked',
    label: 'Course 4',
    modules: [{
      title: 'EBITDA vs Net Income vs Free Cash Flow (Simplified)',
      type: 'video',
      duration: '40 min',
      completed: false
    }, {
      title: 'What Matters at Early vs Growth Stages',
      type: 'video',
      duration: '35 min',
      completed: false
    }, {
      title: 'How Investors Interpret Financial Health',
      type: 'video',
      duration: '30 min',
      completed: false
    }, {
      title: 'Preparing Clean, Decision-Ready Financial Data',
      type: 'reading',
      duration: '25 min',
      completed: false
    }, {
      title: 'Building Internal Financial Discipline Early',
      type: 'exercise',
      duration: '60 min',
      completed: false
    }]
  }, {
    id: 5,
    title: 'Cap Table Strategy & Fundraising Readiness',
    theme: 'Build a company investors can say yes to',
    outcome: 'Structure, maintain, and clean your cap table to avoid future fundraising friction.',
    keyShift: 'A messy cap table can kill a deal — even if the business is strong',
    icon: Briefcase,
    status: 'locked',
    label: 'Course 5',
    modules: [{
      title: 'Cap Table Fundamentals',
      type: 'video',
      duration: '35 min',
      completed: false
    }, {
      title: 'Hidden Dangers: Stacked SAFEs & Complex Preferred Shares',
      type: 'video',
      duration: '40 min',
      completed: false
    }, {
      title: 'Liquidation Waterfalls Explained',
      type: 'video',
      duration: '30 min',
      completed: false
    }, {
      title: 'How to "Clean" a Cap Table Before a Raise',
      type: 'reading',
      duration: '25 min',
      completed: false
    }, {
      title: 'Investor Red Flags & Deal Breakers',
      type: 'reading',
      duration: '20 min',
      completed: false
    }, {
      title: 'Exercise: Structure a Round for Simplicity',
      type: 'exercise',
      duration: '90 min',
      completed: false
    }]
  }, {
    id: 6,
    title: 'Scaling the Team & Operational Readiness',
    theme: 'Build the company your next stage requires',
    outcome: 'Evolve your team and operations to match growth.',
    keyShift: '"The team that got you here won\'t get you there"',
    icon: BookOpen,
    status: 'locked',
    label: 'Course 6',
    modules: [{
      title: 'Why Early Teams Break at Scale',
      type: 'video',
      duration: '35 min',
      completed: false
    }, {
      title: 'Hiring for the Next Stage (Not the Current One)',
      type: 'video',
      duration: '30 min',
      completed: false
    }, {
      title: 'Identifying Skill Gaps in Leadership',
      type: 'video',
      duration: '35 min',
      completed: false
    }, {
      title: 'Building Scalable Org Structures',
      type: 'reading',
      duration: '25 min',
      completed: false
    }, {
      title: 'Operational Readiness: Data Rooms & Reporting',
      type: 'reading',
      duration: '20 min',
      completed: false
    }, {
      title: 'Exercise: Present Like an Institutional-Grade Business',
      type: 'exercise',
      duration: '120 min',
      completed: false
    }]
  }];
  const current = courses[activeCourse];
  const completedModules = current.modules.filter(m => m.completed).length;
  const totalModules = current.modules.length;
  const overallCompleted = courses.reduce((acc, c) => acc + c.modules.filter(m => m.completed).length, 0);
  const overallTotal = courses.reduce((acc, c) => acc + c.modules.length, 0);
  const overallPercent = Math.round(overallCompleted / overallTotal * 100);
  return <div className="space-y-8 animate-in fade-in duration-500" data-id="element-375">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 border-b border-gray-200 pb-6" data-id="element-376">
        <div data-id="element-377">
          <h1 className="text-3xl font-heading font-bold text-catalyst-black tracking-tight" data-id="element-378">
            Curriculum
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-379">
            6 executive playbooks to scale your business with precision
          </p>
        </div>
        <div className="text-right" data-id="element-380">
          <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-381">
            Overall Progress
          </p>
          <div className="flex items-center gap-3" data-id="element-382">
            <div className="w-40 h-2.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-383">
              <div className="bg-catalyst-mustard h-full rounded-full transition-all duration-700" style={{
              width: `${overallPercent}%`
            }} data-id="element-384"></div>
            </div>
            <span className="text-sm font-bold text-catalyst-mustard-dark" data-id="element-385">
              {overallPercent}%
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8" data-id="element-386">
        {/* Course Sidebar */}
        <div className="w-full lg:w-80 shrink-0" data-id="element-387">
          <h2 className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-4 px-2" data-id="element-388">
            Core Courses
          </h2>
          <div className="space-y-2" data-id="element-389">
            {courses.map((course, idx) => {
            const Icon = course.icon;
            const courseCompleted = course.modules.filter(m => m.completed).length;
            const courseTotal = course.modules.length;
            const isActive = activeCourse === idx;
            return <button key={course.id} onClick={() => course.status !== 'locked' && setActiveCourse(idx)} className={`w-full text-left p-4 rounded-xl transition-all border group ${isActive ? 'bg-white border-catalyst-mustard shadow-md' : course.status === 'locked' ? 'bg-gray-50 border-transparent opacity-50 cursor-not-allowed' : 'bg-transparent border-transparent hover:bg-white hover:border-gray-200 hover:shadow-sm'}`} data-id="element-390">
                  <div className="flex items-start gap-3" data-id="element-391">
                    <div className={`p-2 rounded-lg shrink-0 ${isActive ? 'bg-catalyst-mustard/15 text-catalyst-mustard-dark' : course.status === 'completed' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`} data-id="element-392">
                      <Icon size={18} data-id="element-393" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-394">
                      <div className="flex justify-between items-center mb-1" data-id="element-395">
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${isActive ? 'text-catalyst-mustard-dark' : 'text-gray-400'}`} data-id="element-396">
                          {course.label}
                        </span>
                        {course.status === 'completed' && <CheckCircle size={14} className="text-green-500" data-id="element-397" />}
                        {course.status === 'locked' && <Lock size={14} className="text-gray-400" data-id="element-398" />}
                      </div>
                      <h3 className={`text-sm font-semibold leading-snug ${isActive ? 'text-catalyst-black' : 'text-gray-700'}`} data-id="element-399">
                        {course.title}
                      </h3>
                      {course.status !== 'locked' && <div className="mt-2 flex items-center gap-2" data-id="element-400">
                          <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden" data-id="element-401">
                            <div className="h-full bg-catalyst-mustard rounded-full" style={{
                        width: `${courseCompleted / courseTotal * 100}%`
                      }} data-id="element-402"></div>
                          </div>
                          <span className="text-[10px] font-bold text-gray-400" data-id="element-403">
                            {courseCompleted}/{courseTotal}
                          </span>
                        </div>}
                    </div>
                  </div>
                </button>;
          })}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0" data-id="element-404">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-405">
            {/* Course Header */}
            <div className="bg-catalyst-black p-8 md:p-10 text-white relative overflow-hidden" data-id="element-406">
              <div className="absolute top-0 right-0 w-80 h-80 bg-catalyst-mustard/15 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" data-id="element-407"></div>
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-catalyst-mustard/10 rounded-full blur-2xl -ml-10 -mb-10 pointer-events-none" data-id="element-408"></div>

              <div className="relative z-10" data-id="element-409">
                <div className="flex items-center gap-3 mb-5" data-id="element-410">
                  <span className="inline-block px-3 py-1 bg-catalyst-mustard/20 rounded-full text-xs font-bold text-catalyst-mustard tracking-wider uppercase border border-catalyst-mustard/30" data-id="element-411">
                    {current.label}
                  </span>
                  {current.status === 'completed' && <span className="inline-block px-3 py-1 bg-green-500/20 rounded-full text-xs font-bold text-green-400 border border-green-500/30" data-id="element-412">
                      Completed
                    </span>}
                  {current.status === 'active' && <span className="inline-block px-3 py-1 bg-catalyst-mustard/20 rounded-full text-xs font-bold text-catalyst-mustard border border-catalyst-mustard/30" data-id="element-413">
                      In Progress
                    </span>}
                </div>

                <h2 className="text-2xl md:text-3xl font-bold mb-3 leading-tight" data-id="element-414">
                  {current.title}
                </h2>

                <p className="text-catalyst-mustard font-semibold text-sm mb-4 italic" data-id="element-415">
                  "{current.theme}"
                </p>

                <p className="text-gray-400 max-w-2xl leading-relaxed" data-id="element-416">
                  {current.outcome}
                </p>

                <div className="flex flex-wrap gap-6 mt-8 pt-6 border-t border-white/10" data-id="element-417">
                  <div className="flex items-center gap-2 text-sm text-gray-300" data-id="element-418">
                    <BookOpen size={16} className="text-catalyst-mustard" data-id="element-419" />
                    <span data-id="element-420">{totalModules} Modules</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-300" data-id="element-421">
                    <Clock size={16} className="text-catalyst-mustard" data-id="element-422" />
                    <span data-id="element-423">
                      {current.modules.reduce((acc, m) => acc + parseInt(m.duration), 0)}{' '}
                      min total
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-300" data-id="element-424">
                    <CheckCircle size={16} className="text-catalyst-mustard" data-id="element-425" />
                    <span data-id="element-426">
                      {completedModules}/{totalModules} Complete
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Insight */}
            <div className="mx-6 md:mx-8 -mt-0 mb-0" data-id="element-427">
              <div className="bg-catalyst-mustard/10 border border-catalyst-mustard/20 rounded-xl p-4 flex items-start gap-3 mt-6" data-id="element-428">
                <Lightbulb size={20} className="text-catalyst-mustard-dark shrink-0 mt-0.5" data-id="element-429" />
                <div data-id="element-430">
                  <p className="text-xs font-bold text-catalyst-mustard-dark uppercase tracking-wider mb-1" data-id="element-431">
                    Key Insight
                  </p>
                  <p className="text-sm text-catalyst-brown-dark font-medium" data-id="element-432">
                    {current.keyShift}
                  </p>
                </div>
              </div>
            </div>

            {/* Modules List */}
            <div className="p-6 md:p-8 pt-6" data-id="element-433">
              <h3 className="text-lg font-bold text-catalyst-black mb-5" data-id="element-434">
                Course Modules
              </h3>
              <div className="space-y-3" data-id="element-435">
                {current.modules.map((mod, idx) => <div key={idx} className={`flex items-center p-4 rounded-xl border transition-all ${mod.completed ? 'bg-gray-50 border-gray-100' : 'bg-white border-gray-200 hover:border-catalyst-mustard/40 hover:shadow-sm cursor-pointer'}`} data-id="element-436">
                    <div className="mr-4 shrink-0" data-id="element-437">
                      {mod.completed ? <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center text-green-600" data-id="element-438">
                          <CheckCircle size={18} data-id="element-439" />
                        </div> : <div className="w-9 h-9 rounded-full bg-catalyst-mustard/10 flex items-center justify-center text-catalyst-mustard-dark border border-catalyst-mustard/20" data-id="element-440">
                          <span className="text-sm font-bold" data-id="element-441">{idx + 1}</span>
                        </div>}
                    </div>

                    <div className="flex-1 min-w-0" data-id="element-442">
                      <h4 className={`font-semibold text-sm ${mod.completed ? 'text-gray-500' : 'text-catalyst-black'}`} data-id="element-443">
                        {mod.title}
                      </h4>
                      <div className="flex items-center gap-3 mt-1.5" data-id="element-444">
                        <span className="flex items-center gap-1.5 text-xs text-gray-500" data-id="element-445">
                          {mod.type === 'video' ? <PlayCircle size={12} className="text-catalyst-mustard" data-id="element-446" /> : mod.type === 'exercise' ? <FileText size={12} className="text-catalyst-mustard" data-id="element-447" /> : <FileText size={12} className="text-gray-400" data-id="element-448" />}
                          <span className="capitalize font-medium" data-id="element-449">
                            {mod.type}
                          </span>
                        </span>
                        <span className="w-1 h-1 rounded-full bg-gray-300" data-id="element-450"></span>
                        <span className="flex items-center gap-1 text-xs text-gray-500" data-id="element-451">
                          <Clock size={12} data-id="element-452" />
                          {mod.duration}
                        </span>
                      </div>
                    </div>

                    <div className="ml-4 shrink-0" data-id="element-453">
                      <button className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${mod.completed ? 'text-gray-500 hover:bg-gray-200 border border-gray-200' : 'bg-catalyst-mustard text-catalyst-black hover:bg-catalyst-mustard-light shadow-sm'}`} data-id="element-454">
                        {mod.completed ? 'Review' : 'Start'}
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}